export interface TransactionDetails {
  id: string;
  toPublicKey: string;
  fromPublicKey: string;
  transactionType: TransactionType;
  transactionSubType: string;
  amount: number;
  state: string;
  symbol: string;
  ref: string;
}

/** As \SCV.A.Web\ClientApp\src\models\HistoricTransaction.ts TransactionType */
export enum TransactionType {
  WITHDRAW = 'WITHDRAW',
  DELEGATE = 'DELEGATE',
  SIGN = 'SIGN',
  TRANSFER_OWNERSHIP = 'TRANSFER_OWNERSHIP',
  MANAGE = 'MANAGE'
}

/** As
 * \SCV.Constants\TransactionSubType.cs
 * \SCV.A.Web\ClientApp\src\models\Transactions.ts
 */
export enum TransactionSubType {
  Unknown = 'UNKNOWN',
  /** When receiver should know that there is not a SubType for a given TransactionType */
  NotUsed = 'NOTUSED',

  /** All currencies - withdraw */
  WithdrawTransfer = 'WITHDRAW_TRANSFER',

  // Delegate subtypes
  /**
   * Generic Delegate dlg - Create delegation
   * DOT: Bond (to external) */
  DelegateStart = 'DELEGATE_START',
  /**
   * Generic Delegate dlg - Stop delegation (change to 0-address)
   * DOT: Will call SetController with a 0-address
   */
  DelegateStop = 'DELEGATE_STOP',
  /**
   * NEAR: Owner can Pause pool staking
   */
  DelegatePause = 'DELEGATE_PAUSE',
  /**
   * NEAR: Owner can Resume pool staking
   */
  DelegateResume = 'DELEGATE_RESUME',
  /**
    * Generic Delegate dlg - Change delegate address to other external address
    * DOT: Will call SetController with 0-address
    * NEAR: Owner can Update current public staking key to the new given public key
    */
  DelegateEdit = 'DELEGATE_EDIT',

  // DOT specific delegate subtypes
  /**
   * Generic Delegate dlg - Change delegate address
   * DOT: Will call SetController with from-address (change to self/internal controller)
   */
  DelegateSetController = 'DELEGATE_SETCONTROLLER',
  /** DOT: Bond (to self)+Nominate (validator group) */
  DelegateBondNominate = 'DELEGATE_BONDNOMINATE',
  /** DOT: Nominate: After auto-chill: Change Nominations to other validator group */
  DelegateNominate = 'DELEGATE_NOMINATE',
  /** DOT: Change Nominations to other validator group */
  DelegateChillNominate = 'DELEGATE_CHILLNOMINATE',
  /** DOT: Bond more */
  DelegateBondExtra = 'DELEGATE_BONDEXTRA',
  DelegateBondExtraNominate = 'DELEGATE_BONDEXTRANOMINATE',
  /** DOT: Unbond bonded */
  DelegateUnbond = 'DELEGATE_UNBOND',
  /** DOT: Chill+Unbond bonded - when unbonding below minimum delegation amount */
  DelegateChillUnbond = 'DELEGATE_CHILLUNBOND',
  /** DOT: Withdraw unbonded */
  DelegateWithdraw = 'DELEGATE_WITHDRAW',
  /** DOT: Rebond unbonded */
  DelegateRebond = 'DELEGATE_REBOND',
  /** DOT */
  ManageAddProxy = 'MANAGE_ADDPROXY',
  /** DOT */
  ManageRemoveProxy = 'MANAGE_REMOVEPROXY',
}

/** See
 * https://hostmasterbitcoinsuisse.sharepoint.com/sites/Team.Vault-DEV-Team/Shared Documents/Development and Testing/Development Documentation
 * SCV-Api.xlsx - tab: TransactionSubTypes - col: Friendly name
 */
export const buildDelegationTransactionText = (transactionSubType: string) => {
  switch (transactionSubType) {
    case TransactionSubType.DelegateBondNominate:
      return 'Bond funds and nominate';
    case TransactionSubType.DelegateChillNominate:
    case TransactionSubType.DelegateNominate:
      return 'Change nomination';
    case TransactionSubType.DelegateBondExtra:
      return 'Increase bonded funds';
    case TransactionSubType.DelegateUnbond:
    case TransactionSubType.DelegateChillUnbond:
      return 'Unbond bonded funds';
    case TransactionSubType.DelegateWithdraw:
      return 'Withdraw delegation funds';
    case TransactionSubType.DelegateSetController:
      return 'Change delegation controller';
    case TransactionSubType.DelegateRebond:
      return 'Rebond delegation funds';
    case TransactionSubType.DelegateEdit:
      return 'Re-allocate delegation';
    case TransactionSubType.DelegateStop:
      return 'Stop delegation';
    case TransactionSubType.DelegateStart:
      return 'Start delegation';
    case TransactionSubType.DelegatePause:
      return 'Pause delegation';
    case TransactionSubType.DelegateResume:
      return 'Pause delegation';
    case TransactionSubType.ManageAddProxy:
      return 'Add Proxy';
    case TransactionSubType.ManageRemoveProxy:
      return 'Remove Proxy';
    default:
      return 'Delegate';
  }
};

export const hasAmount = (transactionSubType: string): boolean => {
  switch (transactionSubType) {
    case TransactionSubType.ManageAddProxy:
    case TransactionSubType.ManageRemoveProxy:
      return false;
    default:
      return true;
  }
};
